import React, { useState, useEffect, useMemo } from "react";
import _ from "lodash";
import {
    Box,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Checkbox,
    FormControlLabel,
    RadioGroup,
    Radio,
    Button,
    Typography,
    Divider,
    FormHelperText,
    Autocomplete,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import paymentportalservice from "../../../../services/paymentportalservice";

function FileField({ label, onChange, inputId, value }) {
    return (
        <Box>
            <Typography
                variant="caption"
                fontWeight={700}
                component="div"
                sx={TEXT_FIELD_FONT}
            >
                {label}
            </Typography>

            <Button
                variant="outlined"
                component="label"
                size="small"
                fullWidth
                startIcon={<CloudUploadIcon fontSize="small" />}
                sx={{
                    mt: 0.5,
                    textTransform: "none",
                    justifyContent: "flex-start"
                }}
            >
                {value?.name || "Choose file (.pdf)"}
                <input
                    id={inputId}
                    type="file"
                    hidden
                    accept=".pdf"
                    onChange={onChange}
                    style={TEXT_FIELD_FONT}
                />
            </Button>
        </Box>
    );
};

const getVendorLabel = (vendor) => {
    if (!vendor) return "";
    if (typeof vendor === "string") return vendor;
    return vendor.name || vendor.label || "";
};

const TEXT_FIELD_FONT = {
    fontSize: 13,
    fontFamily: "Quicksand"
};

function ExpenseCompanyForm({
    onSubmit,
    form,
    userData,
    gst,
    handleGSTChange,
    customStyle, // kept for compatibility with Brand (react-select) props
    customStyles, // kept for compatibility with Brand (react-select) props
    handleChange,
    validateNumber,
    campaignDetails,
    handleVendorChange,
    handleCalcPercentage,
    paymentLinkToggle,
    handleInvoiceUpload,
    handleScreenshotUpload,
    invoiceFile,
    screenshotFile
}) {
    const [companyData, setCompanyData] = useState([]);

    const getInit = async () => {
        const companies = await paymentportalservice.FetchVendors();
        setCompanyData(companies);
    };

    useEffect(() => {
        getInit();
    }, []);

    const selectedVendor = useMemo(() => {
        if (!userData.company) return null;

        return (
            companyData.find(c => c._id === userData.company) ?? null
        );
    }, [companyData, userData.company]);

    return (
        <Box component="form" onSubmit={onSubmit} ref={form}>
            <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ width: "50%" }}>
                    <Autocomplete
                        disablePortal

                        options={companyData}

                        value={selectedVendor}

                        getOptionLabel={getVendorLabel}

                        isOptionEqualToValue={(option, value) =>
                            getVendorLabel(option) === getVendorLabel(value)
                        }

                        onChange={(_, value) => handleVendorChange(value)}

                        sx={{
                            width: { xs: "100%" },
                            '& .MuiInputBase-input': TEXT_FIELD_FONT,
                            '& .MuiFormLabel-root': TEXT_FIELD_FONT
                        }}

                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Vendor*"
                                placeholder="Vendor*"
                                size="small"
                            />
                        )}

                        ListboxProps={{
                            style: {
                                ...TEXT_FIELD_FONT
                            }
                        }}
                    />
                </Box>
                <TextField
                    fullWidth
                    size="small"
                    sx={{ width: "50%" }}
                    id="formGroupExampleInput"
                    placeholder="Instagram Username*"
                    name="username"
                    value={campaignDetails.name}
                    disabled
                    inputProps={{
                        style: TEXT_FIELD_FONT
                    }}
                />
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Description */}
            <TextField
                fullWidth
                multiline
                minRows={2}
                placeholder="Description*"
                name="description"
                onChange={handleChange}
                value={userData.description}
                required
                sx={{ "& textarea": { resize: "none" } }}
                inputProps={{
                    style: TEXT_FIELD_FONT
                }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Expense category */}
            <TextField
                fullWidth
                size="small"
                id="formGroupExampleInput2"
                placeholder="Type of Expenses (If Applicable)"
                name="expensesCategory"
                value={userData.expensesCategory}
                onChange={handleChange}
                inputProps={{
                    style: TEXT_FIELD_FONT
                }}
            />

            <Divider sx={{ my: 2 }} />

            {/* Bank details */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        size="small"
                        id="user_account_name"
                        placeholder="Account Name*"
                        name="accountName"
                        value={userData.accountName ?? ""}
                        onChange={handleChange}
                        required
                        inputProps={{
                            pattern: "[a-zA-Z\\s]*",
                            title: "Enter only Alphabet Characters",
                            style: TEXT_FIELD_FONT
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        size="small"
                        id="user_account_number"
                        placeholder="Account Number*"
                        name="accountNo"
                        value={userData.accountNo ?? ""}
                        onChange={handleChange}
                        required
                        inputProps={{
                            maxLength: 20,
                            pattern: "[0-9]*",
                            title: "Enter only Numerical Letters",
                            style: TEXT_FIELD_FONT
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        size="small"
                        id="user_ifsc"
                        placeholder="IFSC Code*"
                        name="ifscCode"
                        value={userData.ifscCode ?? ""}
                        onChange={handleChange}
                        required
                        inputProps={{
                            maxLength: 11,
                            pattern: "[A-Z]{4}0[A-Z0-9]{6}",
                            title: "Enter only '4' Uppercase Alphabet Characters, '0' Numerial Letter and '6' Uppercase Alphanumeric Letters",
                            style: TEXT_FIELD_FONT
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        size="small"
                        id="user_pancard"
                        placeholder="Pan Card Number*"
                        name="pancard"
                        value={userData.pancard ?? ""}
                        onChange={handleChange}
                        required
                        inputProps={{
                            maxLength: 10,
                            pattern: "[A-Z]{5}[0-9]{4}[A-Z]{1}",
                            title: "Enter only '5' Uppercase Characters, '4' Numerical Letters and '1' Uppercase Character",
                            style: TEXT_FIELD_FONT
                        }}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Finance / TDS */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        id="user_finance"
                        placeholder="Finance (If Applicable)"
                        name="finance"
                        value={userData.finance}
                        onChange={handleChange}
                        inputProps={{
                            maxLength: 20,
                            pattern: "[0-9]*",
                            title: "Enter only Numerical Letters",
                            style: TEXT_FIELD_FONT
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        size="small"
                        id="user_tds"
                        placeholder="TDS (If Applicable)"
                        name="tds"
                        value={userData.tds}
                        onChange={handleChange}
                        inputProps={{
                            maxLength: 20,
                            pattern: "[0-9]*",
                            title: "Enter only Numerical Letters",
                            style: TEXT_FIELD_FONT
                        }}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* GST checkbox */}
            <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            id="formCheckGst"
                            onClick={handleGSTChange}
                            checked={gst}
                        />
                    }
                    label="GST (If Applicable)"
                />
            </Box>

            {/* Cost / GST / Payable / Percentage */}
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        size="small"
                        id="formGroupExampleInput"
                        placeholder={
                            gst
                                ? "0"
                                : `Total Amount* Your Limit is ${campaignDetails.paymentLimit} Rs`
                        }
                        name="cost"
                        value={userData.cost ?? ""}
                        onChange={handleChange}
                        onKeyUp={validateNumber}
                        required={!gst}
                        inputProps={{
                            style: TEXT_FIELD_FONT
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        size="small"
                        id="formGroupExampleInput"
                        placeholder={gst ? "GST Amount*" : "GST Amount"}
                        name="gstAmount"
                        value={!gst ? "" : userData.gstAmount}
                        onChange={handleChange}
                        onKeyUp={validateNumber}
                        disabled
                        required={gst}
                        inputProps={{
                            style: TEXT_FIELD_FONT
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <TextField
                        fullWidth
                        size="small"
                        id="formGroupExampleInput2"
                        placeholder="Payable Amount*"
                        name="payableAmount"
                        value={userData.payableAmount ?? ""}
                        onChange={handleChange}
                        onKeyUp={validateNumber}
                        disabled
                        required
                        inputProps={{
                            style: TEXT_FIELD_FONT
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small" required>
                        <Select
                            displayEmpty
                            name="percentage"
                            defaultValue=""
                            onChange={handleCalcPercentage}
                            sx={TEXT_FIELD_FONT}
                        >
                            <MenuItem value="" sx={TEXT_FIELD_FONT}>%</MenuItem>
                            {paymentLinkToggle &&
                                _.range(1, parseInt(userData.percentage) + 1).map(
                                    (value, index) => (
                                        <MenuItem value={value} key={index} sx={TEXT_FIELD_FONT}>
                                            {`${value}%`}
                                        </MenuItem>
                                    )
                                )}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Invoice / Screenshot / Payment type */}
            <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                    <FileField
                        label="Invoice (If Applicable)"
                        inputId="formGroupExampleInput"
                        onChange={handleInvoiceUpload}
                        value={invoiceFile}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <FileField
                        label="Screenshot (If Applicable)"
                        inputId="formGroupExampleInput"
                        onChange={handleScreenshotUpload}
                        value={screenshotFile}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Typography
                        variant="caption"
                        fontWeight={700}
                        component="div"
                        sx={TEXT_FIELD_FONT}
                    >
                        Private or LLP*
                    </Typography>

                    <RadioGroup
                        row
                        name="typeOfPayment"
                        onChange={handleChange}
                    >
                        <FormControlLabel
                            value="P"
                            control={<Radio id="flexRadioDefault1" required />}
                            label="P"
                        />
                        <FormControlLabel
                            value="L"
                            control={<Radio id="flexRadioDefault2" />}
                            label="L"
                            style={TEXT_FIELD_FONT}
                        />
                    </RadioGroup>
                </Grid>
            </Grid>

            {/* Submit */}
            <Box sx={{ textAlign: "center", my: 4 }}>
                <Button
                    type="submit"
                    variant="contained"
                    color="secondary"
                    sx={{ width: { xs: "40%", sm: "33%" } }}
                >
                    Submit
                </Button>
                <FormHelperText id="id_fail" error sx={{ display: "none", textAlign: "center" }}>
                    Failure...
                </FormHelperText>
            </Box>
        </Box>
    );
}

export default ExpenseCompanyForm;